import React from "react";
import { useTranslation } from "react-i18next";
import Layout from '../components/Layout';
import { base44 } from "api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Users, Package, Euro, Calendar, Target } from "lucide-react";
import { format } from "date-fns";

import StatCard from "../components/dashboard/StatCard";
import GuestForecast from "../components/dashboard/GuestForecast";
import RevenueForecast from "../components/dashboard/RevenueForecast";
import LowStockAlert from "../components/dashboard/LowStockAlert";
import TaskSummary from "../components/dashboard/TaskSummary";

export default function Dashboard() {
  const { t } = useTranslation();
  const { data: sales, isLoading: loadingSales } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list('-date'),
    initialData: [],
  });

  const { data: inventory, isLoading: loadingInventory } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: tasks, isLoading: loadingTasks } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => base44.entities.Task.list('-due_date'),
    initialData: [],
  });

  // Forecast data for today's stat cards
  const { data: forecastResponse } = useQuery({
    queryKey: ['forecast', 'guests', 7],
    queryFn: () => base44.forecast.getGuests(7),
    placeholderData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000,
  });

  const forecasts = forecastResponse?.forecasts || [];
  const todayForecast = forecasts.length > 0 ? forecasts[0] : null;

  // Calculate stats
  const today = format(new Date(), 'yyyy-MM-dd');
  const todaySales = sales.filter(sale => sale.date === today);
  const todayRevenue = todaySales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
  const todayGuests = todaySales.reduce((sum, sale) => sum + (sale.guests_count || 0), 0);

  const lowStockCount = inventory.filter(item => item.current_quantity <= item.reorder_point).length;

  const todayTasksCount = tasks.filter(task => task.due_date === today && !task.completed).length;

  // Calculate actual trends from data
  const calculateTrends = () => {
    const todayDayOfWeek = new Date().getDay();
    const lastWeekSales = sales.filter(sale => {
      const saleDate = new Date(sale.date);
      const diffDays = (new Date(today) - saleDate) / (1000 * 60 * 60 * 24);
      return diffDays >= 7 && diffDays < 14 && saleDate.getDay() === todayDayOfWeek;
    });

    const lastWeekRevenue = lastWeekSales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
    const lastWeekGuests = lastWeekSales.reduce((sum, sale) => sum + (sale.guests_count || 0), 0);

    const revenueTrend = lastWeekRevenue > 0
      ? ((todayRevenue - lastWeekRevenue) / lastWeekRevenue * 100).toFixed(1)
      : null;
    const guestsTrend = lastWeekGuests > 0
      ? ((todayGuests - lastWeekGuests) / lastWeekGuests * 100).toFixed(1)
      : null;

    return {
      revenueTrend: revenueTrend !== null ? `${revenueTrend > 0 ? '+' : ''}${revenueTrend}%` : t("dashboard.basedOnHistory"),
      revenueTrendDirection: revenueTrend !== null ? (parseFloat(revenueTrend) >= 0 ? 'up' : 'down') : 'up',
      guestsTrend: guestsTrend !== null ? `${guestsTrend > 0 ? '+' : ''}${guestsTrend}%` : t("dashboard.basedOnHistory"),
      guestsTrendDirection: guestsTrend !== null ? (parseFloat(guestsTrend) >= 0 ? 'up' : 'down') : 'up',
    };
  };

  const trends = calculateTrends();

  // Use API forecast data for today's stat cards
  const forecastGuests = todayForecast?.guests || 0;
  const forecastRevenue = todayForecast?.revenue || 0;

  const isLoading = loadingSales || loadingInventory || loadingTasks;

  return (
    <Layout>
      <div className="p-6 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              {t("dashboard.welcomeBack")}
            </h1>
            <p className="text-slate-500 text-lg">
              {format(new Date(), "EEEE, d. MMMM yyyy")}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <StatCard
              title={t("dashboard.forecastGuestsToday")}
              value={forecastGuests}
              icon={Target}
              trend="up"
              trendValue={todayForecast?.confidence ? `${todayForecast.confidence} confidence` : t("dashboard.basedOnHistory")}
              bgColor="bg-indigo-500"
            />
            <StatCard
              title={t("dashboard.forecastRevenueToday")}
              value={`EUR ${forecastRevenue.toFixed(2)}`}
              icon={Calendar}
              trend="up"
              trendValue={t("dashboard.basedOnHistory")}
              bgColor="bg-cyan-500"
            />
            <StatCard
              title={t("dashboard.revenueToday")}
              value={`EUR ${todayRevenue.toFixed(2)}`}
              icon={Euro}
              trend={trends.revenueTrendDirection}
              trendValue={trends.revenueTrend}
              bgColor="bg-green-500"
            />
            <StatCard
              title={t("dashboard.guestsToday")}
              value={todayGuests}
              icon={Users}
              trend={trends.guestsTrendDirection}
              trendValue={trends.guestsTrend}
              bgColor="bg-blue-500"
            />
            <StatCard
              title={t("dashboard.lowStock")}
              value={lowStockCount}
              icon={Package}
              trend={lowStockCount > 0 ? "down" : "up"}
              trendValue={`${lowStockCount} ${t("dashboard.attention")}`}
              bgColor="bg-orange-500"
            />
            <StatCard
              title={t("dashboard.openTasks")}
              value={todayTasksCount}
              icon={TrendingUp}
              bgColor="bg-purple-500"
            />
          </div>

          {/* Main Grid - Revenue Forecast */}
          <div className="mb-6">
            <RevenueForecast />
          </div>

          {/* Secondary Grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <GuestForecast />
            <LowStockAlert inventoryItems={inventory} />
          </div>

          <div className="mt-6">
            <TaskSummary tasks={tasks} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
